









































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ReportDetails } from '@/models/reports/report-details';
import { namespace, Getter } from 'vuex-class';
import { Getters } from '@/stores/reports/getter-constants';
import { Forbidden } from '@/models/forbidden-error';
import { NotFound } from '@/models/not-found-error';
import { Mutations } from '@/stores/reports/mutation-constants';
import { DateTime } from 'luxon';
import { dateParse } from '@/date-picker-parser';
import loading from '@/components/loading.vue';
import { Permissions } from '@/models/permission-constants';
import { ReportApi } from '@/api/report-api';
import { ReportDetailsViewModel } from '@/models/reports/report-details-viewmodel';
import { DisplayColumn } from '@/models/reports/report-display-column';
import { OrderColumn } from '@/models/reports/report-order-column';
import { AggregateFunction } from '@/models/reports/report-column-aggregate-function';
import { FilterGroup } from '@/models/reports/filter-group';
import { AttributeTreeData } from '@/models/reports/attribute-tree-data';
import { Filter } from '@/models/reports/filter';
import { User } from '@/models/user';
import { Field } from '@/models/reports/report-field';
import { FieldSelectorSelection } from '@/models/field-selector/field-selector-selection';
import { QueryResult } from '@/models/reports/report-query-result';
import ReportResults from '@/components/reports/report-results.vue';
import ReportOrderColumns from '@/components/reports/report-order-columns.vue';
import ReportResultFiltering from '@/components/reports/report-results-filtering.vue';
import ReportDisplayColumns from '@/components/reports/report-display-columns.vue';
import VideoPlayerModal from '@/components/video-player/video-player-modal.vue';
import { SortDirection } from '@/models/reports/sort-direction';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

const reportApi = new ReportApi();
const ReportModule = namespace('report');
const UserModule = namespace('user');
const GROUPBY_AGGREGATE = 6;

@Component({
    components: {
        loading,
        ReportResults,
        ReportOrderColumns,
        ReportResultFiltering,
        ReportDisplayColumns,
        VideoPlayerModal,
        ValidationProvider,
        ValidationObserver,
    },
})
export default class ReportView extends Vue {
    @Prop({ type: Boolean, default: false }) public isLoading: boolean;
    @Prop({ type: ReportDetailsViewModel, default: null }) public report: ReportDetailsViewModel;
    @UserModule.Getter('user') public currentUser: User;

    private isSaving: boolean = false;
    private loading: boolean = this.isLoading;
    private showingFieldsPage: boolean = false;
    private showingFiltersPage: boolean = false;
    private showingResultsPage: boolean = false;
    private showVideoPlayer: boolean = false;
    private saving: boolean = false;
    private executing: boolean = false;
    private downloading: boolean = false;
    private selectedColumns: Array<Field> = [];
    private filterGroups: Array<FilterGroup> = [];
    private notifications: Array<string> = [];
    private errorMessages: Array<string> = [];
    private aggregateColumnTypes: Array<any> = [];
    private orderColumns: Array<OrderColumn> = [];
    private showResults: boolean = false;
    private queryResult: QueryResult = new QueryResult();
    private isQueryValid: boolean = false;

    private get canShowFiltersPage() {
        return this.hasColumns;
    }
    private get canShowResultsPage() {
        return this.hasColumns;
    }
    private toObject(): any {
        return {
            id: this.report.id,
            name: this.report.name,
            description: this.report.description,
            selectedTags: this.report.selectedTags.slice(0).sort,
            groupId: this.report.groupId,
            editGroupid: this.report.editGroupId != null && this.report.editGroupId >= 0 ? this.report.editGroupId : null,
            editUserId: this.report.editGroupId === -2 ? this.report.editUserId
                : this.report.editGroupId === -1 ? this.currentUser.id : null,
            resultsPerPage: this.report.resultsPerPage,
            createdBy: this.currentUser.id,
            columns: this.selectedColumns.map((column: any) => {
                return {
                    Column: column,
                    AggregateType: this.report.includeAggregateColumns ? column.AggregateType : null,
                };
            }),
            filterGroups: this.filterGroups.map((filterGroup: any, index) => {
                return filterGroup;
            }),
            orderColumns: this.orderColumns.map((orderColumn: any) => {
                return orderColumn;
            }),
        };
    }

    public async mounted(): Promise<void> {
        this.loading = true;

        this.Populate(this.report);

        if (this.report.id != null && this.report.id > 0) {
            this.showResultsPage();
        } else {
            this.showFieldsPage();
        }
        this.loading = false;
    }

    private get hasNotifications(): boolean {
        return this.notifications.length > 0;
    }

    private get hasErrors(): boolean {
        return this.errorMessages.length > 0;
    }

    private get hasColumns(): boolean {
        return this.selectedColumns.length > 0;
    }

    private get hasSelectedTags(): boolean {
        return this.report.selectedTags.length > 0;
    }

    private get hasOrderColumns(): boolean {
        return this.orderColumns.length > 0;
    }

    private get hasFilterGroups(): boolean {
        return this.filterGroups.length > 0;
    }

    private showFiltersPage() {
        if (this.hasColumns) {
            this.ensureQueryUpToDate(false);
            this.showingResultsPage = false;
            this.showingFieldsPage = false;
            this.showingFiltersPage = true;
        }
    }

    private showFieldsPage() {
        this.showingFiltersPage = false;
        this.showingResultsPage = false;
        this.showingFieldsPage = true;
    }

    private showResultsPage() {
        if (this.hasColumns) {
            this.showingFiltersPage = false;
            this.showingFieldsPage = false;
            this.showingResultsPage = true;
            this.execute();
        }
    }

    private turnOffAggregateColumns() {
        this.report.includeAggregateColumns = false;
    }

    private turnOnAggregateColumns() {
        this.report.includeAggregateColumns = true;
    }

    private dismissNotifications() {
        this.notifications = [];
    }

    private addError(error: string): void {
        this.errorMessages.push(error);
    }

    private setErrors(errors: Array<string>): void {
        this.errorMessages = errors;
    }

    private clearErrors(): void {
        this.errorMessages = [];
    }

    private addNotification(message: string): void {
        this.notifications.push(message);
    }

    private onReordered(e?: any): void {
    }
    private ensureQueryUpToDate(clearResults: boolean): void {
        this.filterGroups.forEach((filterGroup) => {
            const indexes = filterGroup.filters.map((filter, index) => {
                const match = filter.field == null ||
                    (filter.showValue && (!filter.value || filter.value.length === 0)) ||
                    (filter.isVariable && (!filter.variableName || filter.variableName.length === 0));

                if (match) {
                    return index;
                }
            });

            indexes.forEach((index) => {
                if (index !== undefined) {
                    filterGroup.filters.splice(index, 1);
                }
            });
        });

        const filterGroupIndexes = this.filterGroups.map((filterGroup, index) => {
            const match = filterGroup.filters.length === 0;

            if (match) {
                return index;
            }
        });

        filterGroupIndexes.forEach((index) => {
            if (index !== undefined) {
                this.filterGroups.splice(index, 1);
            }
        });

        const orderColumnsIndexes = this.orderColumns.map((column, index) => {
            if (column.field == null) {
                return index;
            }

            if (column.field.entityTypeId != null && this.selectedEntities.indexOf(column.field.entityTypeId) < 0) {
                return index;
            }

            if (this.report.includeAggregateColumns) {
                const allowedColumn = this.selectedColumns.filter((selectedColumn) => {
                    return selectedColumn.name === column.field!.name && selectedColumn.isAggregate;
                });

                if (allowedColumn == null || allowedColumn.length < 1) {
                    return index;
                } else if (allowedColumn[0] == null) {
                    return index;
                }
            }
        });

        orderColumnsIndexes.forEach((index) => {
            if (index !== undefined) {
                this.orderColumns.splice(index, 1);
            }
        });

        if (clearResults) {
            this.showResults = false;
        }

        if (this.orderColumns.length === 0 && this.selectedColumns.length > 0) {
            const column = this.selectedColumns.find((col) => {
                return !col.isAggregate;
            });

            // TODO: this might break the world
            this.addOrderColumn(new OrderColumn(column!));
        }
    }

    private hasSiblingFilterGroup(filterGroup: FilterGroup): boolean {
        return this.filterGroups.length > 1 && this.filterGroups[0] !== filterGroup;
    }

    private addFilter(filterGroup: FilterGroup): void {
        filterGroup.filters.push(new Filter(filterGroup.id));
    }

    private removeFilter(filterGroup: FilterGroup): void {
        // filter removed on group. replacing old group with new group
        const groupIndex = this.filterGroups.findIndex((x) => x.id === filterGroup.id);

        if (groupIndex === -1) {
            return;
        }
        this.filterGroups.splice(groupIndex, 1, filterGroup as FilterGroup);
    }

    private addFilterGroup(): void {
        let hasSibling = false;
        if (this.filterGroups.length > 0) {
            hasSibling = true;
        }
        const filterGroup = new FilterGroup('And', undefined, hasSibling);
        this.filterGroups.push(filterGroup);
    }

    private removeFilterGroup(id: string): void {
        const index = this.filterGroups.findIndex((x) => x.id === id);
        this.filterGroups.splice(index, 1);
    }

    private addOrderColumn(column: OrderColumn): void {
        if (!column) {
            column = new OrderColumn(null);
        }
        this.orderColumns.push(column);
    }

    private removeOrderColumn(column: OrderColumn): void {
        const index = this.orderColumns.indexOf(column);
        this.orderColumns.splice(index, 1);
    }

    private Populate(newData: ReportDetailsViewModel): void {
        this.report.operators = newData.operators;
        this.report.logicalOperators = newData.logicalOperators;
        this.report.orderDirections = newData.orderDirections;
        this.report.booleanValues = newData.booleanValues;
        this.report.aggregateColumnTypes = newData.aggregateColumnTypes;
        this.report.groups = newData.groups;
        this.report.editGroups = newData.groups;

        this.report.editGroups.splice(0, 0, {
            disabled: false,
            selected: false,
            text: 'Me',
            value: '-1',
        });

        this.report.id = newData.id;
        this.report.name = newData.name;
        this.report.description = newData.description;
        this.report.selectedTags = newData.selectedTags;
        this.report.groupId = newData.groupId;
        this.report.editGroupId = newData.editGroupId;
        this.report.editUserId = newData.editUserId;
        this.report.canEdit = newData.canEdit;

        if (this.report.editUserId != null) {
            if (this.currentUser.id !== this.report.editUserId) {
                this.report.editGroups.splice(1, 0, {
                    disabled: false,
                    selected: false,
                    text: 'Report Owner',
                    value: '-2',
                });

                if (this.report.editGroupId == null) {
                    this.report.editGroupId = -2;
                }
            } else {
                if (this.report.editGroupId == null) {
                    this.report.editGroupId = -1;
                }
            }
        }

        if (newData.columns.length > 0 && newData.columns[0].aggregateType) {
            this.report.includeAggregateColumns = true;
        } else {
            this.report.includeAggregateColumns = false;
        }

        this.report.resultsPerPage = newData.resultsPerPage;

        if (newData.columns != null && newData.columns.length > 0) {
            this.selectedColumns = newData.columns.map((column) => {
                return new Field({ ...column.column, aggregateType: column.aggregateType });
            });
        }

        if (newData.filterGroups != null && newData.filterGroups.length > 0) {
            this.filterGroups = newData.filterGroups.map((group, index) => {
                let hasSibling = false;
                if (index !== 0) {
                    hasSibling = true;
                }
                return new FilterGroup(group.logicalOperator, group.filters, hasSibling);
            });
        }

        if (newData.orderColumns != null && newData.orderColumns.length > 0) {
            this.orderColumns = newData.orderColumns.map((column) => {
                const directionText = column.direction === '0' ? 'Ascending' : 'Descending';
                return new OrderColumn(column.field, directionText);
            });
        }

        this.queryResult = new QueryResult();
    }

    private async execute(): Promise<void> {
        if (this.executing) {
            return;
        }

        this.ensureQueryUpToDate(false);
        this.clearErrors();
        this.showResults = false;
        this.isQueryValid = true;
        this.executing = true;

        try {
            this.queryResult = await reportApi.runAsync(this.toObject());
        } catch (e) {
            this.executing = false;
            this.addError('Failed to execute report');
        }
        this.executing = false;
        this.showResults = true;
    }

    private get hasUniqueColumns(): boolean {
        const selectedColumnCount = this.selectedColumns.length;
        const uniqueSelectedColumnCount = this.selectedColumns.filter((column, index) => {
            return this.selectedColumns.findIndex((c) => c.currentDisplayName === column.currentDisplayName && c.entityTypeName === column.entityTypeName) === index;
        }).length;

        return selectedColumnCount === uniqueSelectedColumnCount;
    }

    private get populatedOrderColumns() {
        return this.orderColumns.filter((column) => {
            return column.field != null;
        });
    }

    private validate(): boolean {
        let valid = true;

        if (this.selectedColumns.length < 1) {
            this.addError('You must select at least one column to display in the report');
            valid = false;
        }

        if (this.report.includeAggregateColumns) {
            const nonAggregateFields = this.selectedColumns.filter((field) => {
                return !field.isAggregate;
            });

            if (nonAggregateFields.length < 1) {
                this.addError('You must include a column to group results by');
                valid = false;
            }
        }

        if (this.report.resultsPerPage < 1) {
            this.addError('You must display at least 1 record per page');
            valid = false;
        }

        if (this.report.resultsPerPage > 200) {
            this.addError('You can display no more than 200 records per page');
            valid = false;
        }

        if (!this.hasUniqueColumns) {
            this.addError('All selected column display names must be unique');
            valid = false;
        }

        if (this.populatedOrderColumns.length < 1) {
            this.addError('You must select at least one column to order results by');
            valid = false;
        }

        if (this.report.name == null || this.report.name.length < 1) {
            this.addError('You must give the report a valid name');
            valid = false;
        }

        return valid;
    }

    private async save(): Promise<void> {
        if (this.saving) {
            return;
        }

        const successMessage = 'Report successfully saved.';
        const index = this.notifications.indexOf(successMessage);
        if (index > -1) {
            this.notifications.splice(index, 1);
        }

        this.ensureQueryUpToDate(false);
        this.clearErrors();

        this.saving = true;

        // TODO: handle failure
        const response = await reportApi.saveAsync(this.toObject());

        this.report.id = response.id;
        this.addNotification('Report successfully saved.');

        this.saving = false;
    }

    private async downloadReport(): Promise<void> {
        this.ensureQueryUpToDate(false);
        this.clearErrors();
        this.isQueryValid = true;

        this.downloading = true;
        try {
            const file = await reportApi.downloadAsync(this.toObject());
            const url = window.URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = this.report.name + '.xlsx';
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
            this.downloading = false;
        } catch (e) {
            this.downloading = false;
            this.addError('An error occured while attempting to download the report.');
        }
    }
    private get selectedEntities(): Array<number | null | undefined> {
        return this.selectedColumns.map((column) => {
            return column.entityTypeId;
        }).filter((id, index, array) => {
            return array.indexOf(id) === index;
        });
    }

    private handleOrderColumnsReordered(orderColumns: Array<OrderColumn>) {
        if (orderColumns.length === this.orderColumns.length) {
            this.orderColumns = orderColumns;
        }
    }

    private updateSelectedColumns(selectedColumns: Array<Field>) {
        this.selectedColumns = selectedColumns;

        const tables: Array<string> = this.selectedColumns.map((column) => {
            return column.entityTypeName;
        });

        if (tables.length === 0) {
            return;
        }

        const filterGroupsIndexesToRemove: Array<number> = new Array<number>();

        this.filterGroups.forEach((group, index) => {
            let removeGroup: boolean = false;
            group.filters.forEach((filter) => {
                if (filter.field != null) {
                    if (tables.indexOf(filter.field.entityTypeName) < 0) {
                        removeGroup = true;
                    }
                }
            });
            if (removeGroup) {
                filterGroupsIndexesToRemove.push(index);
            }
        });

        if (filterGroupsIndexesToRemove.length > 0) {
            filterGroupsIndexesToRemove.forEach((index) => {
                this.filterGroups.splice(index, 1);
            });
        }
    }

    private openVideoPlayer() {
        this.showVideoPlayer = true;
    }
    private closeVideoPlayer() {
        this.showVideoPlayer = false;
    }
}
